import React from 'react';
import FundInvestmentInvestor from '../../../models/admin/FundInvestmentData';
import { Permissions } from '../../../models/admin/Permission';
import StyledSection from '../../styles/StyledSection';
import AddInvestorModal from '../AddInvestorModal';
import type { InvestorListRowContact } from './InvestorListRow';
import KanbanBoard from './KanbanBoard';
import type { BulkPromoteStage } from './investorManagementTypes';

export interface InvestorManagementMainContentProps {
  loaded: boolean;
  data?: {
    opportunity: { id?: string };
    investors: FundInvestmentInvestor[];
  };
  highlightedInvestorId?: string;
  investorFilter: string;
  onInvestorFilterChange: (filter: string) => void;
  hasPermission: (permissionKey: Permissions) => boolean;
  showAddInvestor: boolean;
  onShowAddInvestorChange: (show: boolean) => void;
  onDataRefresh: () => void;
  addAllInProgress: boolean;
  bulkPromoteInProgress: boolean;
  bulkPromoteStage?: FundInvestmentInvestor['status'];
  investorActionInProgress?: string;
  actionTypeInProgress?: 'delete' | 'promote' | 'demote';
  onAddAllCandidates: () => void;
  onBulkPromote: (stage: BulkPromoteStage, investorIds?: string[]) => void;
  getContactsForStage: (
    stage: FundInvestmentInvestor['status'],
    options?: { withInvestment?: boolean; withoutInvestment?: boolean },
  ) => InvestorListRowContact[];
}

export default function InvestorManagementMainContent({
  loaded,
  data,
  highlightedInvestorId,
  investorFilter,
  onInvestorFilterChange,
  hasPermission,
  showAddInvestor,
  onShowAddInvestorChange,
  onDataRefresh,
  addAllInProgress,
  bulkPromoteInProgress,
  bulkPromoteStage,
  investorActionInProgress,
  actionTypeInProgress,
  onAddAllCandidates,
  onBulkPromote,
  getContactsForStage,
}: InvestorManagementMainContentProps) {
  if (showAddInvestor) {
    const opportunityId = data?.opportunity.id;
    if (!opportunityId) {
      return null;
    }

    return (
      <StyledSection tone="tone1">
        <AddInvestorModal
          fundOpportunityId={opportunityId}
          onCancel={() => onShowAddInvestorChange(false)}
          onComplete={() => {
            onShowAddInvestorChange(false);
            onDataRefresh();
          }}
        />
      </StyledSection>
    );
  }

  return (
    <KanbanBoard
      loaded={loaded}
      investors={data?.investors || []}
      highlightedInvestorId={highlightedInvestorId}
      investorFilter={investorFilter}
      onInvestorFilterChange={onInvestorFilterChange}
      hasPermission={hasPermission}
      onAddInvestor={() => onShowAddInvestorChange(true)}
      onAddAllCandidates={onAddAllCandidates}
      onBulkPromote={onBulkPromote}
      addAllInProgress={addAllInProgress}
      bulkPromoteInProgress={bulkPromoteInProgress}
      bulkPromoteStage={bulkPromoteStage}
      investorActionInProgress={investorActionInProgress}
      actionTypeInProgress={actionTypeInProgress}
      getContactsForStage={getContactsForStage}
    />
  );
}
